import React, { useMemo } from "react";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
);

const chartContainerStyle = {
    width: "100%",
    display: "flex",
    justifyContent: "center",
};

const chartFrameStyle = {
    width: "95%",
    maxWidth: "95%",
    height: "280px",
    borderRadius: "8px",
    border: "1px solid #E8E8E8",
    backgroundColor: "#FFFFFF",
    padding: "10px 8px",
};

function toNumberOrNull(value) {
    if (value === null || value === undefined) return null;
    const v = typeof value === "string" ? value.trim() : value;
    if (v === "" || v === "N/A" || v === "—") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

function getTimeValue(r) {
    // Prefer already-composed labels if backend provides them.
    return (
        r?.label ??
        r?.time_label ??
        r?.timeLabel ??
        r?.datetime_display ??
        r?.timestamp_display ??
        r?.date_time_display ??
        // Otherwise try to compose from date + time fields
        (r?.date_display && r?.time_display
            ? `${r.date_display} ${r.time_display}`
            : r?.date_display && r?.time
              ? `${r.date_display} ${r.time}`
              : r?.date && r?.time
                ? `${r.date} ${r.time}`
                : r?.time ??
                  r?.timestamp ??
                  r?.datetime ??
                  r?.t ??
                  "")
    );
}

function formatXAxisLabel(raw) {
    if (!raw) return "";
    const str = String(raw).trim();

    // Convert common backend time display "HH.mm" => "HH:mm"
    // Example: "15.50" -> "15:50", "16.03" -> "16:03"
    return str.replace(/(\d{1,2})\.(\d{2})/g, (_, hh, mm) => {
        return `${String(hh).padStart(2, "0")}:${mm}`;
    });
}

function getSpo2Value(r) {
    return (
        r?.spo2 ??
        r?.SpO2 ??
        r?.sPO2 ??
        r?.spO2 ??
        r?.spo2_percent ??
        r?.spo2Percent ??
        r?.SPO2 ??
        null
    );
}

function getHrValue(r) {
    return r?.hr ?? r?.HR ?? r?.heart_rate ?? r?.heartRate ?? r?.pulse ?? null;
}

function normalizeTrendToRows(trend) {
    if (!trend) return [];
    if (Array.isArray(trend)) return trend;
    if (Array.isArray(trend?.rows)) return trend.rows;
    if (Array.isArray(trend?.data)) return trend.data;
    if (Array.isArray(trend?.values)) return trend.values;
    if (Array.isArray(trend?.records)) return trend.records;

    // Support object shapes with { labels/label, spo2, hr } arrays
    const labelsArray =
        (Array.isArray(trend?.labels) && trend.labels) ||
        (Array.isArray(trend?.label) && trend.label) ||
        (Array.isArray(trend?.time_labels) && trend.time_labels) ||
        null;

    if (
        labelsArray &&
        Array.isArray(trend?.spo2) &&
        Array.isArray(trend?.hr)
    ) {
        return labelsArray.map((lab, i) => ({
            time: lab,
            spo2: trend.spo2[i],
            hr: trend.hr[i],
        }));
    }

    // Alternate key casing
    if (
        labelsArray &&
        Array.isArray(trend?.SpO2) &&
        Array.isArray(trend?.HR)
    ) {
        return labelsArray.map((lab, i) => ({
            time: lab,
            spo2: trend.SpO2[i],
            hr: trend.HR[i],
        }));
    }

    return [];
}

export default function SleepSpo2HrChart({
    dropEpisodes = [],
    reportData,
    maxPoints,
    maxXLabelCount = 8,
}) {
    const { labels, spo2Data, hrData } = useMemo(() => {
        const epRows = (dropEpisodes || [])
            .flatMap((ep) => ep?.rows || [])
            .filter(Boolean);

        const reportRows = normalizeTrendToRows(reportData);

        // Prefer trend/report data if provided; otherwise fallback to dropEpisodes.
        let allRows = reportRows.length ? reportRows : epRows;
        const sliceCount = typeof maxPoints === "number" ? maxPoints : null;
        if (sliceCount != null && sliceCount > 0) {
            allRows = allRows.slice(0, sliceCount);
        }
        if (!allRows.length) {
            return { labels: [], spo2Data: [], hrData: [] };
        }

        const labels = allRows.map((r) => formatXAxisLabel(getTimeValue(r)));
        const spo2Data = allRows.map((r) => toNumberOrNull(getSpo2Value(r)));
        const hrData = allRows.map((r) => toNumberOrNull(getHrValue(r)));

        return { labels, spo2Data, hrData };
    }, [dropEpisodes, reportData, maxPoints]);

    const hasData = labels.length > 0 && (spo2Data.some((v) => v != null) || hrData.some((v) => v != null));

    // Reduce x-axis label clutter while keeping ALL data points visible.
    const labelStep = (() => {
        if (!labels.length) return 1;
        const safeMax = Math.max(2, Number(maxXLabelCount) || 8);
        if (labels.length <= safeMax) return 1;
        return Math.ceil(labels.length / safeMax);
    })();

    const chartData = {
        labels,
        datasets: [
            {
                label: "SpO₂ (%)",
                data: spo2Data,
                borderColor: "#2F6BFF",
                backgroundColor: "#2F6BFF",
                borderDash: [0, 0],
                tension: 0.35,
                pointRadius: 0,
                yAxisID: "y",
            },
            {
                label: "Pulse Rate (bpm)",
                data: hrData,
                borderColor: "#F2A100",
                backgroundColor: "#F2A100",
                borderDash: [0, 0],
                tension: 0.35,
                pointRadius: 0,
                yAxisID: "y1",
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: "index",
            intersect: false,
        },
        plugins: {
            legend: {
                position: "top",
                labels: {
                    font: { family: "Poppins", size: 12, weight: 500 },
                    generateLabels: (chart) => {
                        const defaultLabels =
                            ChartJS.defaults.plugins.legend.labels.generateLabels(
                                chart,
                            );
                        return defaultLabels.map((label) => ({
                            ...label,
                            color: label.strokeStyle,
                        }));
                    },
                },
            },
            tooltip: {
                enabled: true,
            },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: "Time",
                    font: { family: "Poppins" },
                },
                ticks: {
                    maxRotation: 75,
                    minRotation: 75,
                    autoSkip: false,
                    callback: (value, index) => {
                        // Show ~maxXLabelCount labels, evenly spaced across the time range.
                        if (index % labelStep !== 0) return "";
                            return labels?.[index] ?? value;
                    },
                },
                grid: {
                    color: "#EDEDED",
                },
            },
            y: {
                type: "linear",
                position: "left",
                min: 50,
                max: 100,
                title: {
                    display: true,
                    text: "Pulse Rate (bpm)",
                    font: { family: "Poppins", weight: "bold" },
                    color: "#F2A100",
                },
                grid: {
                    color: "#EDEDED",
                },
            },
            y1: {
                type: "linear",
                position: "right",
                min: 50,
                max: 100,
                title: {
                    display: true,
                    text: "SpO₂ (%)",
                    font: { family: "Poppins", weight: "bold" },
                    color: "#2F6BFF",
                },
                grid: {
                    drawOnChartArea: false,
                    color: "#EDEDED",
                },
            },
        },
    };

    if (!hasData) {
        return (
            <div style={chartContainerStyle}>
                <div style={chartFrameStyle}>
                    <div
                        style={{
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontFamily: "Poppins",
                            color: "#777",
                            fontSize: "13px",
                        }}
                    >
                        No chart data
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={chartContainerStyle}>
            <div style={chartFrameStyle}>
                <Line data={chartData} options={options} />
            </div>
        </div>
    );
}

